import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Helmet } from 'react-helmet';
import * as ethers from 'ethers';
import axios from 'axios';

import ZwalletConnection from './ZwalletConnection';
import ZwalletTabs from './ZwalletTabs';
import ZwalletModals from './ZwalletModals';
import { SecureStorage, PasswordManager } from './ZwalletSecurity';
import { useZwalletTransactions } from './ZwalletTransactions';
import { useZwalletTokens } from './ZwalletTokens';

// ADD THIS IMPORT
import { AuthContext } from '../contexts/AuthContext';

// Etherscan API Configuration
const ETHERSCAN_API_KEY = '6HJSDA2M6N7T5WX7VVMSAF3JHW7YQ8GZ3G';
const ETHERSCAN_BASE_URL = 'https://api.etherscan.io/api';

// ==================== SIMPLE FREE RPC CONFIGURATION ====================
// Free public RPC endpoints - no API keys needed
const FREE_RPC_ENDPOINTS = [
  'https://rpc.ankr.com/eth/dec8e5317af4d9c8cb41cc5bfaef26700319860751215bc62794cd3cafdd691d', // ✅ Your key
  'https://ethereum.publicnode.com',  // ✅ Backup 1
  'https://cloudflare-eth.com'        // ✅ Backup 2
];

// Simple function to get a working RPC provider
const getRPCProvider = async () => {
  console.log('🔗 Getting RPC provider...');
  
  // Try each endpoint until one works
  for (const endpoint of FREE_RPC_ENDPOINTS) {
    try {
      console.log(`🔄 Trying: ${endpoint}`);
      const provider = new ethers.JsonRpcProvider(endpoint);
      
      // Quick test - get network info (3 second timeout)
      const networkPromise = provider.getNetwork();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 3000)
      );
      
      await Promise.race([networkPromise, timeoutPromise]);
      console.log(`✅ Connected to: ${endpoint}`);
      return provider;
    } catch (error) {
      console.log(`⚠️ Failed: ${endpoint} - ${error.message}`);
    }
  }
  
  // If all fail, use the first one as fallback
  console.log('⚠️ All RPCs failed, using fallback');
  return new ethers.JsonRpcProvider(FREE_RPC_ENDPOINTS[0]);
};

// Simple RPC URL getter for backward compatibility
const getRPCUrl = () => FREE_RPC_ENDPOINTS[0];
// ==================== END RPC CONFIGURATION ====================

// Common ERC20 ABI
const ERC20_ABI = [
  "function balanceOf(address) view returns (uint)",
  "function transfer(address to, uint amount) returns (bool)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function name() view returns (string)"
];

// ==================== BACKGROUND MODE FOR ZHAT ====================
// 🎯 SURGICAL FIX: Add background mode detection
const isBackgroundMode = window.location.search.includes('background') ||
                        window.location.search.includes('hideUI=true');

if (isBackgroundMode) {
  console.log('🌙 Zwallet running in background mode for Zhat');

  // Hide UI elements
  document.documentElement.style.display = 'none';

  // Or minimal UI
  document.body.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--black);
      color: var(--gold);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      opacity: 0.7;
    ">
      🔐 Zwallet running in background for Zhat encryption
    </div>
  `;

  // Store the current address (will be updated when wallet connects)
  let currentAddress = 'unknown';

  // Listen for background initialization requests
  window.addEventListener('message', (event) => {
    if (!event.data || event.data.source !== 'zhat-app') return;

    if (event.data.type === 'BACKGROUND_WALLET_REQUEST') {
      console.log('✅ Background wallet initialization requested');

      // Respond that we're ready
      event.source.postMessage({
        source: 'zwallet',
        type: 'BACKGROUND_WALLET_READY',
        address: currentAddress,
        ready: true,
        timestamp: Date.now()
      }, event.origin);

      // Also announce encryption API
      setTimeout(() => {
        event.source.postMessage({
          source: 'zwallet',
          type: 'ENCRYPTION_API_READY',
          address: currentAddress,
          api: 'zhatEncryption',
          timestamp: Date.now(),
          backgroundMode: true
        }, event.origin);
      }, 1000);
    }

    if (event.data.type === 'HEARTBEAT') {
      // Respond to heartbeat
      event.source.postMessage({
        source: 'zwallet',
        type: 'HEARTBEAT_RESPONSE',
        timestamp: Date.now()
      }, event.origin);
    }

    if (event.data.type === 'CLOSE_BACKGROUND_WALLET') {
      console.log('👋 Closing background wallet per Zhat request');
      window.close();
    }
  });

  // Function to update address when wallet connects
  window.__UPDATE_BACKGROUND_WALLET_ADDRESS__ = (address) => {
    currentAddress = address;
    console.log('📝 Background wallet address updated:', address);
  };

  // Prevent accidental closing
  window.onbeforeunload = (event) => {
    if (window.__ZWALLET_BACKGROUND_MODE__) {
      // Don't show confirmation in background mode
      return null;
    }
  };

  window.__ZWALLET_BACKGROUND_MODE__ = true;
  console.log('✅ Background mode initialized for Zhat');
}
// ==================== END BACKGROUND MODE ====================

// GlobalNav Component - gold color styling
const GlobalNav = () => {
  return (
    <nav className="global-nav">
      <style>{`
        .global-nav ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: center;
        }
        .global-nav li {
          margin: 0;
        }
        .gold-link {
          color: #FFD700 !important;
          text-decoration: none;
          font-weight: 500;
          font-size: 14px;
          padding: 6px 12px;
          border-radius: 4px;
          transition: all 0.3s ease;
          border: 1px solid transparent;
        }
        .gold-link:hover {
          background: rgba(255, 215, 0, 0.1);
          border: 1px solid #FFD700;
          text-shadow: 0 0 8px rgba(255, 215, 0, 0.5);
        }
      `}</style>
      <ul>
        <li><a href="/" className="gold-link">Home</a></li>
        <li><a href="/wallet" className="gold-link">Wallet</a></li>
        <li><a href="/ens" className="gold-link">ENS</a></li>
        <li><a href="/profile" className="gold-link">Profile</a></li>
        <li><a href="/feed" className="gold-link">Feed</a></li>
        <li><a href="/messages" className="gold-link">Messages</a></li>
        <li><a href="/settings" className="gold-link">Settings</a></li>
      </ul>
    </nav>
  );
};

// Password Modal Component - UPDATED FOR CORRECT FLOW
const PasswordModal = ({
  accountPassword,
  setAccountPassword,
  loading,
  error,
  onCreateAccount,
  onClose
}) => {
  return (
    <div className="zwallet-modal-overlay" style={{zIndex: 1001}}>
      <div className="zwallet-modal">
        <div className="zwallet-modal-header">
          <div className="zwallet-branding">
            <h2>🔐 Set Password</h2>
            <span className="zwallet-subtitle">Step 2: Encrypt your wallet locally</span>
          </div>
          <button className="zwallet-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="zwallet-modal-body" style={{padding: '1.5rem'}}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: 'var(--gold)', marginBottom: '8px', fontWeight: 'bold' }}>
              Set Local Password
            </label>
            <input
              type="password"
              placeholder="Enter a secure password"
              value={accountPassword}
              onChange={(e) => setAccountPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--card-bg)',
                color: 'var(--gold)',
                border: '1px solid var(--gold)',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            />
            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '5px' }}>
              This password only encrypts your wallet on this device
            </div>
          </div>

          <div className="zwallet-exclusive" style={{margin: '20px 0'}}>
            <h4>🔒 Local Protection Only</h4>
            <ul className="exclusive-features">
              <li>Your seed phrase is the master key to all funds</li>
              <li>Password only protects this specific installation</li>
              <li>Recovery requires seed phrase, not password</li>
            </ul>
          </div>
        </div>

        <div className="zwallet-modal-footer">
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCreateAccount(accountPassword);
              }}
              disabled={loading || !accountPassword}
              style={{
                flex: 1,
                background: loading || !accountPassword ? '#666' : 'var(--gold)',
                color: 'var(--black)',
                border: 'none',
                padding: '15px',
                borderRadius: '8px',
                cursor: loading || !accountPassword ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '16px',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              {loading ? 'Encrypting...' : 'Finalize Wallet'}
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              style={{
                flex: 1,
                background: 'transparent',
                color: '#ff4444',
                border: '1px solid #ff4444',
                padding: '15px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              Cancel
            </button>
          </div>

          {error && (
            <div style={{
              background: error.includes('✅') ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 68, 68, 0.1)',
              border: error.includes('✅') ? '1px solid #00ff88' : '1px solid #ff4444',
              color: error.includes('✅') ? '#00ff88' : '#ff4444',
              padding: '12px',
              borderRadius: '5px',
              marginTop: '15px',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          <div style={{
            background: 'rgba(255, 215, 0, 0.05)',
            padding: '15px',
            borderRadius: '8px',
            marginTop: '20px',
            border: '1px solid var(--gold)'
          }}>
            <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '12px', lineHeight: '1.4' }}>
              <strong>Note:</strong> Your password is stored locally and cannot be recovered. Your seed phrase is the only way to recover your wallet.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Seed Phrase Modal Component - ULTRA SIMPLE WORKING VERSION
const SeedPhraseModal = ({
  seedPhrase,
  onClose,
  onContinue
}) => {
  const [hasSaved, setHasSaved] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  // DEBUG
  useEffect(() => {
    console.log('🌱 SeedPhraseModal mounted:', {
      hasSaved,
      seedPhraseWords: seedPhrase ? seedPhrase.split(' ').length : 0
    });
  }, [hasSaved, seedPhrase]);

  // ULTRA SIMPLE checkbox toggle - ONE WAY
  const toggleCheckbox = () => {
    console.log('✅ toggleCheckbox called, current state:', hasSaved);
    const newValue = !hasSaved;
    setHasSaved(newValue);
  };

  // Simple continue handler
  const handleContinue = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('🖱️ Continue clicked, hasSaved:', hasSaved);

    if (!hasSaved) {
      console.log('❌ Blocked: hasSaved is false');
      return;
    }

    console.log('✅ Calling onContinue...');
    onContinue();
  };

  return (
    <div className="zwallet-modal-overlay" style={{zIndex: 1002}}>
      <div className="zwallet-modal" style={{maxWidth: '550px'}}>
        <div className="zwallet-modal-header">
          <div className="zwallet-branding">
            <h2>🌱 Your Recovery Phrase</h2>
            <span className="zwallet-subtitle">Step 1: Save these 12 words in order</span>
          </div>
          <button
            className="zwallet-close-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('❌ Modal closed');
              onClose();
            }}
          >
            ✕
          </button>
        </div>

        <div className="zwallet-modal-body" style={{padding: '1.5rem'}}>
          <div style={{
            background: 'rgba(255, 215, 0, 0.05)',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid var(--gold)'
          }}>
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 10px 0', fontSize: '14px', lineHeight: '1.5' }}>
              <strong>⚠️ THIS IS YOUR MASTER KEY:</strong> These 12 words are the <strong>only way</strong> to recover your wallet. Anyone with these words can steal all your funds.
            </p>
          </div>

          <div style={{
            background: 'var(--black)',
            padding: '20px',
            borderRadius: '10px',
            border: '1px solid var(--gold)',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '10px',
              marginBottom: '15px'
            }}>
              {seedPhrase.split(' ').map((word, index) => (
                <div
                  key={index}
                  style={{
                    background: 'rgba(255, 215, 0, 0.1)',
                    padding: '10px',
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    color: 'var(--gold)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '40px'
                  }}
                >
                  <span style={{
                    color: 'var(--text-secondary)',
                    fontSize: '12px',
                    marginRight: '5px',
                    fontWeight: 'bold'
                  }}>
                    {index + 1}.
                  </span>
                  {word}
                </div>
              ))}
            </div>

            <div style={{ textAlign: 'center', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(seedPhrase);
                  setHasCopied(true);
                  setTimeout(() => setHasCopied(false), 3000);
                }}
                style={{
                  background: 'transparent',
                  color: 'var(--gold)',
                  border: '1px solid var(--gold)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}
              >
                {hasCopied ? '✅ Copied!' : '📋 Copy to Clipboard'}
              </button>

              <button
                onClick={() => {
                  const blob = new Blob([seedPhrase], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'zwallet-seed-phrase.txt';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                style={{
                  background: 'transparent',
                  color: 'var(--gold)',
                  border: '1px solid var(--gold)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}>
                💾 Download as File
              </button>
            </div>
          </div>

          {/* SIMPLIFIED CHECKBOX - NO COMPLEX EVENT HANDLING */}
          <div style={{ marginBottom: '20px', padding: '10px', background: 'rgba(255,215,0,0.05)', borderRadius: '8px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                userSelect: 'none'
              }}
              onClick={toggleCheckbox}
            >
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid var(--gold)',
                  borderRadius: '4px',
                  background: hasSaved ? 'var(--gold)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--black)',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                {hasSaved ? '✓' : ''}
              </div>
              <span style={{ color: 'var(--text-secondary)', fontSize: '14px', flex: 1 }}>
                I have written down my seed phrase and stored it securely
              </span>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '5px', paddingLeft: '30px' }}>
              {hasSaved ? '✅ Ready to continue' : '⬜ Check this box to continue'}
            </div>
          </div>
        </div>

        <div className="zwallet-modal-footer">
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleContinue}
              disabled={!hasSaved}
              style={{
                flex: 1,
                background: hasSaved ? 'var(--gold)' : '#666',
                color: 'var(--black)',
                border: 'none',
                padding: '15px',
                borderRadius: '8px',
                cursor: hasSaved ? 'pointer' : 'not-allowed',
                fontWeight: 'bold',
                fontSize: '16px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                opacity: hasSaved ? 1 : 0.7
              }}
            >
              {hasSaved ? '✅ Continue to Step 2' : '⬜ Check the box above first'}
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              style={{
                flex: 1,
                background: 'transparent',
                color: '#ff4444',
                border: '1px solid #ff4444',
                padding: '15px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              Cancel Creation
            </button>
          </div>

          <div style={{
            background: 'rgba(255, 68, 68, 0.1)',
            padding: '15px',
            borderRadius: '8px',
            marginTop: '20px',
            border: '1px solid #ff4444'
          }}>
            <p style={{ color: '#ff4444', margin: 0, fontSize: '12px', lineHeight: '1.4' }}>
              <strong>⚠️ FINAL WARNING:</strong> Never share your seed phrase with anyone.
            </p>
          </div>

          <div style={{marginTop: '15px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px'}}>
            Step 1 of 2: Recovery phrase secured locally
          </div>
        </div>
      </div>
    </div>
  );
};

// Unlock Modal Component
const UnlockModal = ({
  accountPassword,
  setAccountPassword,
  isUnlocking,
  error,
  onUnlock,
  onLoadAccounts,
  onClose
}) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--card-bg)',
        padding: '30px',
        borderRadius: '15px',
        border: '2px solid var(--gold)',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 0 30px var(--gold)'
      }}>
        <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
          🔓 Unlock Zwallet
        </h3>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: 'var(--gold)', marginBottom: '8px', fontWeight: 'bold' }}>
            Enter Password
          </label>
          <input
            type="password"
            placeholder="Enter your wallet password"
            value={accountPassword}
            onChange={(e) => setAccountPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && accountPassword) {
                onLoadAccounts(accountPassword);
              }
            }}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--black)',
              color: 'var(--gold)',
              border: '1px solid var(--gold)',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '5px' }}>
            Enter the password you set when creating the wallet
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!accountPassword) return;
              onLoadAccounts(accountPassword);
            }}
            disabled={isUnlocking || !accountPassword}
            style={{
              flex: 1,
              background: isUnlocking || !accountPassword ? '#666' : 'var(--gold)',
              color: 'var(--black)',
              border: 'none',
              padding: '15px',
              borderRadius: '8px',
              cursor: isUnlocking || !accountPassword ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            {isUnlocking ? 'Unlocking...' : 'Unlock'}
          </button>
          
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            style={{
              flex: 1,
              background: 'transparent',
              color: '#ff4444',
              border: '1px solid #ff4444',
              padding: '15px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Cancel
          </button>
        </div>
        
        {error && (
          <div style={{
            background: error.includes('✅') ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 68, 68, 0.1)',
            border: error.includes('✅') ? '1px solid #00ff88' : '1px solid #ff4444',
            color: error.includes('✅') ? '#00ff88' : '#ff4444',
            padding: '12px',
            borderRadius: '5px',
            marginTop: '15px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
        
        <div style={{
          background: 'rgba(255, 215, 0, 0.05)',
          padding: '15px',
          borderRadius: '8px',
          marginTop: '20px',
          border: '1px solid var(--gold)'
        }}>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '12px', lineHeight: '1.4' }}>
            <strong>Forgot password?</strong> You can create a new wallet, but you'll lose access to existing accounts.
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Zwallet component
const Zwallet = (props) => {
  const {
    onClose,
    onConnected,
    onWalletCreated,
    initialMode = 'connect',
    skipConnectionScreen = false,
    embedded = false  // ADDED: Embedded mode prop
  } = props;

  console.log('🔧 Zwallet component mounted - Embedded:', embedded, 'Mode:', initialMode, 'skipConnectionScreen:', skipConnectionScreen);

  // ADDED: Get AuthContext function
  const { registerZwallet } = useContext(AuthContext);

  // Mount counter for debugging
  const mountCount = React.useRef(0);
  React.useEffect(() => {
    mountCount.current += 1;
    console.log(`🚀 Zwallet mounted ${mountCount.current} times`);
    
    return () => {
      console.log(`🔄 Zwallet unmounted. Total mounts: ${mountCount.current}`);
    };
  }, []);

  // Refs for state management
  const ensResolutionInProgress = useRef(false);
  const resolvedAccounts = useRef(new Set());
  const lastResolvedAccount = useRef('');
  const resolveTimeout = useRef(null);
  const txConfirmResolversRef = useRef(new Map());
  const pollingRef = useRef(new Set());
  const isMountedRef = useRef(true);
  
  // ADDED: Refs for ENS integration
  const pendingTxResolve = useRef(null);
  const pendingTxReject = useRef(null);
  const externalTransactionQueue = useRef(new Map());
  
  // FIX ADDED: Ref to prevent multiple connection attempts
  const isConnectingRef = useRef(false);
  
  // FIX ADDED: Ref to track initialization runs
  const initializationRun = useRef(false);

  // Core Zwallet state
  const [account, setAccount] = useState('');
  const [balance, setBalance] = useState('0');
  const [ethPrice, setEthPrice] = useState(0);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [network, setNetwork] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showAddToken, setShowAddToken] = useState(false);

  // Local wallet state
  const [localAccounts, setLocalAccounts] = useState([]);
  const [activeLocalAccount, setActiveLocalAccount] = useState(null);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showImportAccountModal, setShowImportAccountModal] = useState(false);
  const [showLocalAccountModal, setShowLocalAccountModal] = useState(false);

  // Seed phrase state - REORDERED FOR CORRECT FLOW
  const [seedPhrase, setSeedPhrase] = useState('');
  const [showSeedPhrase, setShowSeedPhrase] = useState(false);
  const [importSeedPhrase, setImportSeedPhrase] = useState('');

  // Password state - Moved AFTER seed phrase in flow
  const [accountPassword, setAccountPassword] = useState('');
  const [accountType, setAccountType] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [hasExistingAccounts, setHasExistingAccounts] = useState(false);

  // Track pending wallet creation
  const [pendingWallet, setPendingWallet] = useState(null);

  // Transaction state
  const [pendingTransaction, setPendingTransaction] = useState(null);
  const [showTransactionConfirm, setShowTransactionConfirm] = useState(false);
  const [showTransactionReceipt, setShowTransactionReceipt] = useState(false);
  const [transactionReceipt, setTransactionReceipt] = useState(null);

  // ENS State
  const [connectedENSName, setConnectedENSName] = useState('');
  const enableENSResolution = true;

  // Send transaction state
  const [sendTo, setSendTo] = useState('');
  const [sendAmount, setSendAmount] = useState('');

  // Initialization guard
  const [initializationComplete, setInitializationComplete] = useState(false);

  // Use modular hooks
  const {
    txHistory,
    fetchTransactionHistory,
    formatHash,
    formatAddress,
  } = useZwalletTransactions(ETHERSCAN_API_KEY, ETHERSCAN_BASE_URL);

  const {
    tokens,
    newTokenAddress,
    setNewTokenAddress,
    fetchTokenBalances,
    addToken: addTokenHook
  } = useZwalletTokens(ETHERSCAN_API_KEY, ETHERSCAN_BASE_URL);

  // ADDED: Function for ENS components to send transactions
  const sendTransactionForENS = useCallback(async (transactionData) => {
    console.log('🏦 Zwallet: External transaction request from ENS:', transactionData);
    
    return new Promise((resolve, reject) => {
      const messageId = transactionData.messageId || 
        `ens-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      
      // Store resolve/reject for later
      pendingTxResolve.current = resolve;
      pendingTxReject.current = reject;
      externalTransactionQueue.current.set(messageId, { resolve, reject });
      
      // Set up transaction and show confirmation modal
      setSendTo(transactionData.to || '');
      setSendAmount(transactionData.value || '0');
      
      // Create pending transaction object
      const pendingTx = {
        to: transactionData.to,
        value: transactionData.value || '0',
        data: transactionData.data || '0x',
        from: account,
        gasLimit: transactionData.gasLimit || '0x5208',
        chainId: transactionData.chainId || '0x1',
        messageId,
        ...transactionData
      };
      
      setPendingTransaction(pendingTx);
      setShowTransactionConfirm(true);
      
      // Return messageId for tracking
      resolve(messageId);
    });
  }, [account]);

  // ADDED: Function for ENS components to sign messages
  const signMessageForENS = useCallback(async (message) => {
    console.log('✍️ Zwallet: Signing message for ENS:', message);
    
    if (!signer) {
      throw new Error('Wallet not connected');
    }
    
    try {
      const signature = await signer.signMessage(message);
      return signature;
    } catch (error) {
      console.error('❌ Sign message failed:', error);
      throw error;
    }
  }, [signer]);

  // Fetch ETH price
  const fetchEthPrice = useCallback(async () => {
    try {
      console.log('💵 Fetching ETH price...');
      
      // Try CoinGecko API first
      try {
        const coingeckoResponse = await axios.get(
          'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd',
          { timeout: 5000 }
        );

        if (coingeckoResponse.data?.ethereum?.usd) {
          const price = coingeckoResponse.data.ethereum.usd;
          console.log('💵 ETH price fetched from CoinGecko:', price);
          setEthPrice(price);
          return price;
        }
      } catch (coingeckoError) {
        console.warn('❌ CoinGecko price fetch failed:', coingeckoError.message);
      }

      // Fallback to CryptoCompare
      try {
        const response = await fetch(
          'https://min-api.cryptocompare.com/data/price?fsym=ETH&tsyms=USD'
        );
        const data = await response.json();
        const price = data.USD || 0;
        console.log('💵 ETH price fetched from CryptoCompare:', price);
        setEthPrice(price);
        return price;
      } catch (fallbackError) {
        console.warn('❌ CryptoCompare price fetch failed:', fallbackError.message);
      }

      // Final fallback
      console.warn('⚠️ All price APIs failed, using fallback price');
      const fallbackPrice = 3500;
      setEthPrice(fallbackPrice);
      return fallbackPrice;

    } catch (err) {
      console.warn('❌ All ETH price fetch methods failed:', err.message);
      const fallbackPrice = 3500;
      setEthPrice(fallbackPrice);
      return fallbackPrice;
    }
  }, []);

  // Account data management
  const updateAccountData = useCallback(async (providerArg, accountArg) => {
    console.log('🔄 updateAccountData called:', { account: accountArg });

    try {
      setLoading(true);

      // Fetch balance
      console.log('💰 Fetching balance for account:', accountArg);
      const bal = await providerArg.getBalance(accountArg);
      const balanceEth = ethers.formatEther(bal);
      console.log('💰 Balance fetched:', parseFloat(balanceEth));

      setBalance(balanceEth);

      // Fetch ETH price
      await fetchEthPrice().catch(priceError => {
        console.warn('⚠️ ETH price fetch failed but continuing:', priceError.message);
      });

      const networkInfo = await providerArg.getNetwork();
      console.log('🌐 Network info:', networkInfo);
      setNetwork(networkInfo);

      // Fetch token balances and transaction history
      await Promise.allSettled([
        fetchTokenBalances(accountArg),
        fetchTransactionHistory(accountArg)
      ]);

      setLoading(false);
      console.log('✅ Account data updated successfully');

    } catch (err) {
      console.error('❌ Failed to update account data:', err);
      setError('Failed to update account data: ' + err.message);
      setLoading(false);
    }
  }, [fetchTokenBalances, fetchTransactionHistory, fetchEthPrice]);

  // ENS Resolution
  const resolveENSName = useCallback(async (address) => {
    if (!address || !enableENSResolution || ensResolutionInProgress.current) {
      return null;
    }

    ensResolutionInProgress.current = true;
    lastResolvedAccount.current = address;

    try {
      console.log(`🔍 Resolving ENS name for: ${address}`);

      if (resolveTimeout.current) {
        clearTimeout(resolveTimeout.current);
      }

      const timeoutPromise = new Promise((_, reject) => {
        resolveTimeout.current = setTimeout(() => {
          reject(new Error('ENS resolution timeout'));
        }, 10000);
      });

      const resolutionPromise = provider.lookupAddress(address);
      const name = await Promise.race([resolutionPromise, timeoutPromise]);

      if (name && name !== connectedENSName) {
        console.log(`✅ ENS name resolved: ${name}`);
        setConnectedENSName(name);
        resolvedAccounts.current.add(address);
        return name;
      }
      return null;
    } catch (error) {
      console.log('❌ ENS resolution failed:', error.message);
      resolvedAccounts.current.add(address);
      return null;
    } finally {
      ensResolutionInProgress.current = false;
      
      if (resolveTimeout.current) {
        clearTimeout(resolveTimeout.current);
        resolveTimeout.current = null;
      }

      setTimeout(() => {
        if (lastResolvedAccount.current === address) {
          lastResolvedAccount.current = '';
        }
      }, 1000);
    }
  }, [provider, connectedENSName, enableENSResolution]);

  // Single ENS resolution useEffect
  useEffect(() => {
    if (account && isConnected && enableENSResolution) {
      requestAnimationFrame(() => {
        resolveENSName(account);
      });
    } else {
      setConnectedENSName('');
      resolvedAccounts.current.clear();
      lastResolvedAccount.current = '';
    }
  }, [account, isConnected, resolveENSName, enableENSResolution]);

  // ============================================
  // 🔐 HANDLE ENCRYPTION REQUESTS FROM ZHAT
  // ============================================

  useEffect(() => {
    const handleEncryptionRequests = (event) => {
      // 🛠️ UPDATED: More flexible source checking
      if (!event.data) return;

      // Accept messages from multiple sources OR encryption-related messages
      const isValidSource = event.data.source === 'zhat-app' ||
                          event.data.source === 'zpost-app' ||
                          event.data.source === undefined; // For backward compatibility

      const isEncryptionRequest = event.data.type && (
        event.data.type.includes('ENCRYPT') ||
        event.data.type.includes('DECRYPT') ||
        event.data.type === 'GET_PUBLIC_KEY' ||
        event.data.type === 'ENCRYPTION_STATUS_REQUEST' ||
        event.data.type === 'ECDH_DERIVE' ||
        event.data.type === 'GENERATE_EPHEMERAL_KEY'
      );

      // Only process if it's a valid source OR an encryption request
      if (!isValidSource && !isEncryptionRequest) {
        return;
      }

      // DEBUG LOG: See ALL messages that pass through
      console.log('🔐 Zwallet processing message:', {
        source: event.data.source,
        type: event.data.type,
        requestId: event.data.requestId,
        fromOrigin: event.origin
      });

      const { type, requestId, ...data } = event.data;

      console.log('🔐 Zwallet received encryption request:', { type, requestId });

      // Handle encryption requests
      switch (type) {
        case 'ENCRYPT_MESSAGE_REQUEST':
          handleEncryptMessage(requestId, data);
          break;
        case 'DECRYPT_MESSAGE_REQUEST':
          handleDecryptMessage(requestId, data);
          break;
        case 'GET_PUBLIC_KEY_REQUEST':
          handleGetPublicKey(requestId);
          break;
        case 'ECDH_DERIVE':
          handleEcdhDerive(requestId, data);
          break;
        case 'GENERATE_EPHEMERAL_KEY':
          handleGenerateEphemeralKey(requestId);
          break;
        case 'ENCRYPTION_STATUS_REQUEST':
          handleEncryptionStatus(requestId);
          break;
      }
    };

    const handleEncryptMessage = async (requestId, data) => {
      try {
        if (!signer) {
          throw new Error('Wallet not connected');
        }

        const { receiverAddress, message, options = {} } = data;

        console.log('🔐 Encrypting message for:', receiverAddress, {
          messageLength: message?.length,
          options
        });

        // Get user's public key from signer
        const publicKey = signer.signingKey.publicKey;

        // In a real implementation, we would:
        // 1. Get recipient's public key from somewhere (server or local cache)
        // 2. Compute ECDH shared secret
        // 3. Derive symmetric key
        // 4. Encrypt the message with AES-GCM

        // For MVP, we'll do a simple encryption using the private key
        // NOTE: This is NOT production-ready - just for demonstration

        // Create a simple encryption using the private key hash
        const encoder = new TextEncoder();
        const messageBytes = encoder.encode(message);

        // Generate IV (Initialization Vector)
        const iv = crypto.getRandomValues(new Uint8Array(12));

        // Create a simple key from the private key (for demo only!)
        const privateKeyHash = await crypto.subtle.digest(
          'SHA-256',
          encoder.encode(signer.privateKey.slice(2, 34)) // Take part of private key
        );

        // Import key for AES-GCM
        const cryptoKey = await crypto.subtle.importKey(
          'raw',
          privateKeyHash,
          { name: 'AES-GCM' },
          false,
          ['encrypt']
        );

        // Encrypt
        const encrypted = await crypto.subtle.encrypt(
          {
            name: 'AES-GCM',
            iv: iv
          },
          cryptoKey,
          messageBytes
        );

        // Convert to base64 for transmission
        const ciphertext = btoa(String.fromCharCode(...new Uint8Array(encrypted)));
        const ivBase64 = btoa(String.fromCharCode(...iv));

        // Generate salt
        const salt = crypto.getRandomValues(new Uint8Array(16));
        const saltBase64 = btoa(String.fromCharCode(...salt));

        // Send response back to Zhat
        window.parent.postMessage({
          source: 'zwallet',
          type: 'ENCRYPT_MESSAGE_RESPONSE',
          requestId,
          encryptedData: {
            ciphertext,
            iv: ivBase64,
            auth_tag: btoa(''), // GCM includes auth tag in ciphertext
            salt: saltBase64,
            encryption_metadata: {
              version: 'zhat-v1',
              algorithm: 'AES-GCM',
              key_derivation: 'private-key-hash', // For demo only
              timestamp: new Date().toISOString(),
              publicKey: publicKey,
              ...options.metadata
            }
          }
        }, '*');

        console.log('✅ Message encrypted and sent to Zhat');

      } catch (error) {
        console.error('❌ Encryption failed:', error);
        window.parent.postMessage({
          source: 'zwallet',
          type: 'ENCRYPT_MESSAGE_RESPONSE',
          requestId,
          error: error.message
        }, '*');
      }
    };

    const handleDecryptMessage = async (requestId, data) => {
      try {
        if (!signer) {
          throw new Error('Wallet not connected');
        }

        const { senderAddress, encryptedData } = data;

        console.log('🔓 Decrypting message from:', senderAddress);

        // For MVP, we'll do simple decryption using the private key
        const encoder = new TextEncoder();

        // Convert from base64
        const ciphertext = atob(encryptedData.ciphertext);
        const ciphertextBytes = new Uint8Array(ciphertext.length);
        for (let i = 0; i < ciphertext.length; i++) {
          ciphertextBytes[i] = ciphertext.charCodeAt(i);
        }

        const iv = atob(encryptedData.iv);
        const ivBytes = new Uint8Array(iv.length);
        for (let i = 0; i < iv.length; i++) {
          ivBytes[i] = iv.charCodeAt(i);
        }

        // Recreate the key from private key (same as encryption)
        const privateKeyHash = await crypto.subtle.digest(
          'SHA-256',
          encoder.encode(signer.privateKey.slice(2, 34))
        );

        const cryptoKey = await crypto.subtle.importKey(
          'raw',
          privateKeyHash,
          { name: 'AES-GCM' },
          false,
          ['decrypt']
        );

        // Decrypt
        const decrypted = await crypto.subtle.decrypt(
          {
            name: 'AES-GCM',
            iv: ivBytes
          },
          cryptoKey,
          ciphertextBytes
        );

        const decoder = new TextDecoder();
        const decryptedMessage = decoder.decode(decrypted);

        // Send response back to Zhat
        window.parent.postMessage({
          source: 'zwallet',
          type: 'DECRYPT_MESSAGE_RESPONSE',
          requestId,
          decryptedMessage
        }, '*');

        console.log('✅ Message decrypted and sent to Zhat');

      } catch (error) {
        console.error('❌ Decryption failed:', error);
        window.parent.postMessage({
          source: 'zwallet',
          type: 'DECRYPT_MESSAGE_RESPONSE',
          requestId,
          error: error.message
        }, '*');
      }
    };

    const handleGetPublicKey = async (requestId) => {
      try {
        if (!signer) {
          throw new Error('Wallet not connected');
        }

        const publicKey = signer.signingKey.publicKey;

        console.log('🔑 Sending public key to Zhat:', publicKey.substring(0, 32) + '...');

        window.parent.postMessage({
          source: 'zwallet',
          type: 'GET_PUBLIC_KEY_RESPONSE',
          requestId,
          publicKey
        }, '*');

      } catch (error) {
        console.error('❌ Failed to get public key:', error);
        window.parent.postMessage({
          source: 'zwallet',
          type: 'GET_PUBLIC_KEY_RESPONSE',
          requestId,
          error: error.message
        }, '*');
      }
    };

    const handleEcdhDerive = async (requestId, data) => {
      try {
        if (!signer) {
          throw new Error('Wallet not connected');
        }

        const { otherPublicKey } = data;

        console.log('🔑 Computing ECDH shared secret with:', otherPublicKey.substring(0, 32) + '...');

        // In a real implementation, we would use elliptic curve cryptography
        // For MVP, we'll return a placeholder

        // Generate a shared secret placeholder
        const encoder = new TextEncoder();
        const combined = encoder.encode(signer.privateKey.slice(2, 34) + otherPublicKey.slice(2, 34));
        const sharedSecret = await crypto.subtle.digest('SHA-256', combined);

        // Convert to hex
        const sharedSecretArray = Array.from(new Uint8Array(sharedSecret));
        const sharedSecretHex = sharedSecretArray.map(b => b.toString(16).padStart(2, '0')).join('');

        window.parent.postMessage({
          source: 'zwallet',
          type: 'ECDH_DERIVE_RESPONSE',
          requestId,
          sharedSecret: sharedSecretHex
        }, '*');

      } catch (error) {
        console.error('❌ ECDH derivation failed:', error);
        window.parent.postMessage({
          source: 'zwallet',
          type: 'ECDH_DERIVE_RESPONSE',
          requestId,
          error: error.message
        }, '*');
      }
    };

    const handleGenerateEphemeralKey = async (requestId) => {
      try {
        console.log('🔑 Generating ephemeral key pair');

        // Generate a new random wallet for ephemeral key
        const { ethers: _ethers } = await import('ethers');
        const ephemeralWallet = _ethers.Wallet.createRandom();

        window.parent.postMessage({
          source: 'zwallet',
          type: 'GENERATE_EPHEMERAL_KEY_RESPONSE',
          requestId,
          ephemeralKey: {
            privateKey: ephemeralWallet.privateKey,
            publicKey: ephemeralWallet.signingKey.publicKey,
            address: ephemeralWallet.address
          }
        }, '*');

      } catch (error) {
        console.error('❌ Failed to generate ephemeral key:', error);
        window.parent.postMessage({
          source: 'zwallet',
          type: 'GENERATE_EPHEMERAL_KEY_RESPONSE',
          requestId,
          error: error.message
        }, '*');
      }
    };

    const handleEncryptionStatus = async (requestId) => {
      try {
        const status = {
          available: !!signer,
          initialized: !!signer?.signingKey?.publicKey,
          ready: !!signer && isConnected,
          hasPrivateKey: !!signer?.privateKey,
          address: account,
          publicKey: signer?.signingKey?.publicKey,
          timestamp: new Date().toISOString()
        };

        console.log('🔐 Sending encryption status to Zhat:', status);

        window.parent.postMessage({
          source: 'zwallet',
          type: 'ENCRYPTION_STATUS_RESPONSE',
          requestId,
          ...status
        }, '*');

        // Also send a general encryption ready event
        if (signer && isConnected) {
          setTimeout(() => {
            window.parent.postMessage({
              source: 'zwallet',
              type: 'ENCRYPTION_READY',
              address: account,
              publicKey: signer.signingKey.publicKey,
              timestamp: new Date().toISOString()
            }, '*');
          }, 100);
        }

      } catch (error) {
        console.error('❌ Failed to get encryption status:', error);
        window.parent.postMessage({
          source: 'zwallet',
          type: 'ENCRYPTION_STATUS_RESPONSE',
          requestId,
          error: error.message
        }, '*');
      }
    };

    // Add event listener for messages
    window.addEventListener('message', handleEncryptionRequests);

    return () => {
      window.removeEventListener('message', handleEncryptionRequests);
    };
  }, [signer, isConnected, account]);

  // Format address with ENS support
  const formatAddressWithENS = (addr) => {
    if (!addr) return 'Unknown';

    try {
      if (addr.toLowerCase() === account?.toLowerCase() && connectedENSName) {
        return (
          <span title={`${addr} (${connectedENSName})`}>
            <a
              href={`https://etherscan.io/address/${addr}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--gold)',
                textDecoration: 'none',
                fontFamily: 'monospace',
                fontSize: '12px'
              }}
              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              title={`View ${connectedENSName} on Etherscan`}
            >
              🌐 {connectedENSName}
            </a>
          </span>
        );
      }

      const formatted = `${addr.slice(0, 6)}...${addr.slice(-4)}`;
      return (
        <a
          href={`https://etherscan.io/address/${addr}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'var(--gold)',
            textDecoration: 'none',
            fontFamily: 'monospace',
            fontSize: '12px'
          }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          title="View on Etherscan"
        >
          {formatted}
        </a>
      );
    } catch (error) {
      console.warn('Error formatting address with ENS:', error);
      const formatted = `${addr.slice(0, 6)}...${addr.slice(-4)}`;
      return <span>{formatted}</span>;
    }
  };

  const confirmTransaction = async () => {
    if (!pendingTransaction) {
      throw new Error('No pending transaction to confirm');
    }

    setError('');
    try {
      if (!provider || !signer) {
        throw new Error('Wallet provider or signer not available');
      }

      // Prepare transaction request
      const txRequest = {
        to: pendingTransaction.to,
        data: pendingTransaction.data || '0x',
        value: pendingTransaction.value || '0x0',
        gasLimit: pendingTransaction.gasLimit || '0x5208',
        from: pendingTransaction.from || account
      };

      console.debug('[confirmTransaction] sending txRequest', txRequest);
      const txResponse = await signer.sendTransaction(txRequest);

      // ADDED: Resolve external ENS transaction promise
      if (pendingTxResolve.current) {
        pendingTxResolve.current({ 
          success: true, 
          hash: txResponse.hash,
          transactionHash: txResponse.hash,
          messageId: pendingTransaction.messageId
        });
        pendingTxResolve.current = null;
        pendingTxReject.current = null;
        
        // Also resolve from queue
        if (pendingTransaction.messageId) {
          const queued = externalTransactionQueue.current.get(pendingTransaction.messageId);
          if (queued) {
            queued.resolve({
              success: true,
              hash: txResponse.hash,
              transactionHash: txResponse.hash
            });
            externalTransactionQueue.current.delete(pendingTransaction.messageId);
          }
        }
      }

      // Update UI
      setShowTransactionConfirm(false);
      setTransactionReceipt({
        hash: txResponse.hash,
        status: 'pending',
        from: txResponse.from || txRequest.from,
        to: txResponse.to || txRequest.to,
        value: txRequest.value,
        gasUsed: txResponse.gasLimit?.toString?.() || undefined,
        blockNumber: null
      });
      setShowTransactionReceipt(true);

      // Start polling for receipt
      try { pollTransactionReceipt(txResponse.hash); } catch (e) { /* ignore */ }

      return { success: true, hash: txResponse.hash };
    } catch (err) {
      console.error('Transaction failed:', err);
      setError(`❌ Transaction failed: ${err.message || String(err)}`);
      
      // ADDED: Reject external ENS transaction promise
      if (pendingTxReject.current) {
        pendingTxReject.current(new Error(`Transaction failed: ${err.message}`));
        pendingTxResolve.current = null;
        pendingTxReject.current = null;
      }
      
      // Also reject from queue
      if (pendingTransaction?.messageId) {
        const queued = externalTransactionQueue.current.get(pendingTransaction.messageId);
        if (queued) {
          queued.reject(new Error(`Transaction failed: ${err.message}`));
          externalTransactionQueue.current.delete(pendingTransaction.messageId);
        }
      }
      
      throw err;
    } finally {
      setPendingTransaction(null);
    }
  };

  const cancelTransaction = () => {
    const pendingId = pendingTransaction?.id || null;
    if (pendingId) {
      const resolver = txConfirmResolversRef.current.get(pendingId);
      if (resolver && typeof resolver.reject === 'function') {
        resolver.reject(new Error('User cancelled transaction'));
        txConfirmResolversRef.current.delete(pendingId);
      }
    }

    // ADDED: Reject external ENS transaction promise
    if (pendingTxReject.current) {
      pendingTxReject.current(new Error('User cancelled transaction'));
      pendingTxResolve.current = null;
      pendingTxReject.current = null;
    }
    
    // Also reject from queue
    if (pendingTransaction?.messageId) {
      const queued = externalTransactionQueue.current.get(pendingTransaction.messageId);
      if (queued) {
        queued.reject(new Error('User cancelled transaction'));
        externalTransactionQueue.current.delete(pendingTransaction.messageId);
      }
    }

    setShowTransactionConfirm(false);
    setPendingTransaction(null);
  };

  const closeReceipt = () => {
    setShowTransactionReceipt(false);
    setTransactionReceipt(null);
  };

  // FIXED: Connect local account with free RPCs
  const connectLocalAccount = useCallback(async (accountObj) => {
    console.log('🔗 Connecting local account:', accountObj.address);

    // Guard to prevent reconnection to same account
    if (isConnected && account && account.toLowerCase() === accountObj.address.toLowerCase()) {
      console.log('⚠️ Already connected to this account, skipping');
      return;
    }

        // 🎯 ADD THIS: Update background wallet address if in background mode
    if (window.__UPDATE_BACKGROUND_WALLET_ADDRESS__) {
      window.__UPDATE_BACKGROUND_WALLET_ADDRESS__(accountObj.address);
    }

    // Prevent multiple simultaneous connections
    if (isConnectingRef.current) {
      console.log('⏸️ Connection already in progress, skipping');
      return;
    }

    isConnectingRef.current = true;

    try {
      setLoading(true);

      // USE FREE PUBLIC RPC PROVIDER
      const jsonRpcProvider = await getRPCProvider();
      const accountWithProvider = new ethers.Wallet(accountObj.privateKey, jsonRpcProvider);

      // Set states
      setProvider(jsonRpcProvider);
      setSigner(accountWithProvider);
      setAccount(accountObj.address);
      setActiveLocalAccount(accountObj);
      setAccountType('local');

      await updateAccountData(jsonRpcProvider, accountObj.address);
      setIsConnected(true);
      setError('');

      // Close all modals
      setShowLocalAccountModal(false);
      setShowPasswordModal(false);
      setShowUnlockModal(false);
      setShowSeedPhrase(false);
      setShowImportAccountModal(false);

      console.log('✅ Local account connected successfully');

      // 🔐🔐🔐 ADD THIS BLOCK - EXPOSE ENCRYPTION API FOR ZHAT 🔐🔐🔐
      console.log('🔐 Setting up encryption API for Zhat...');

      // Create the encryption API object
      const encryptionAPI = {
        // Status check (used by Zhat initialization)
        getEncryptionStatus: () => ({
          available: true,
          ready: true,
          hasSigner: true,
          address: accountObj.address,
          publicKey: accountWithProvider?.signingKey?.publicKey || 'public-key-placeholder',
          timestamp: new Date().toISOString(),
          walletType: 'zpost-zwallet'
        }),

        // Generate key pair
        generateKeyPair: async () => {
          console.log('🔑 Generating key pair for Zhat');
          try {
            const { ethers: _ethers } = await import('ethers');
            const wallet = _ethers.Wallet.createRandom();
            return {
              publicKey: wallet.signingKey.publicKey,
              privateKey: wallet.privateKey,
              address: wallet.address,
              timestamp: new Date().toISOString()
            };
          } catch (error) {
            console.error('❌ Key generation failed:', error);
            throw error;
          }
        },

        // Derive shared secret (for ECDH)
        deriveSharedSecret: async (privateKey, publicKey) => {
          console.log('🔐 Deriving shared secret for Zhat');
          // Placeholder - replace with real ECDH
          return `shared-secret-${Date.now()}`;
        },

        // Encrypt message
        encryptMessage: async (message, options = {}) => {
          console.log('🔒 Encrypting message for Zhat');
          // Placeholder - replace with real encryption
          return {
            encrypted: btoa(message),
            iv: 'iv-placeholder',
            timestamp: new Date().toISOString(),
            algorithm: 'AES-GCM-placeholder'
          };
        },

        // Decrypt message
        decryptMessage: async (encryptedData, options = {}) => {
          console.log('🔓 Decrypting message for Zhat');
          // Placeholder - replace with real decryption
          return atob(encryptedData.encrypted);
        },

        // Test function to verify API is working
        testConnection: () => ({
          success: true,
          message: 'Zwallet encryption API is ready for Zhat',
          timestamp: new Date().toISOString(),
          address: accountObj.address
        })
      };

      // EXPOSE GLOBALLY - TWO METHODS FOR COMPATIBILITY
      window.zhatEncryption = encryptionAPI;
      window.__zpost_wallet_api__ = {
        ...(window.__zpost_wallet_api__ || {}),
        encryption: encryptionAPI
      };

      console.log('✅ Encryption API exposed globally:', {
        zhatEncryption: !!window.zhatEncryption,
        hasGetStatus: !!window.zhatEncryption?.getEncryptionStatus,
        address: accountObj.address
      });

      // Also signal via localStorage that wallet is ready
      localStorage.setItem('zhat_wallet_ready', JSON.stringify({
        ready: true,
        address: accountObj.address,
        timestamp: new Date().toISOString(),
        hasEncryption: true
      }));

      // Broadcast message to all windows (in case Zhat is listening)
      try {
        window.postMessage({
          source: 'zwallet',
          type: 'ENCRYPTION_API_READY',
          address: accountObj.address,
          timestamp: new Date().toISOString()
        }, '*');
      } catch (e) {
        console.log('⚠️ Could not broadcast message:', e.message);
      }
      // 🔐🔐🔐 END OF ENCRYPTION API BLOCK 🔐🔐🔐

      // Save session for auto-login
      if (accountPassword) {
        PasswordManager.saveSession(accountPassword);
      }

      // FIX: Check if we should do ENS verification based on mode
      const shouldCheckENS = window.location.pathname.includes('zpost') ||
                            (initialMode === 'connect' || initialMode === 'unlock');

      console.log(`🔍 ENS Check Decision: shouldCheckENS=${shouldCheckENS}, initialMode=${initialMode}`);

      // Notify parent component (AuthContext)
      if (onConnected && typeof onConnected === 'function') {
        console.log('🔗 Notifying parent component of connection');
        onConnected({
          address: accountObj.address,
          provider: jsonRpcProvider,
          signer: accountWithProvider,
          isConnected: true,
          shouldCheckENS: shouldCheckENS // Tell AuthContext whether to check ENS
        });
      }

    } catch (error) {
      console.error('❌ Failed to connect local account:', error);
      setError('Failed to connect local account: ' + error.message);
    } finally {
      setLoading(false);
      isConnectingRef.current = false;
    }
  }, [updateAccountData, onConnected, accountPassword, account, isConnected, initialMode]);

  // Check for existing accounts
  useEffect(() => {
    console.log('🔍 Checking for existing accounts...');
    const accountCheck = SecureStorage.hasAccounts();
    console.log('📦 Existing accounts found:', accountCheck);
    setHasExistingAccounts(accountCheck);
  }, []);

  // FIXED: STANDARD WALLET CREATION FLOW - SEED PHRASE FIRST, PASSWORD SECOND
  const handleGenerateSeedPhrase = async () => {
    console.log('🌱 Generating seed phrase (STEP 1)...');
    try {
      setLoading(true);
      setError('');
      
      const { ethers: _ethers } = await import('ethers');
      const wallet = _ethers.Wallet.createRandom();
      
      console.log('✅ Seed phrase generated:', wallet.mnemonic.phrase);
      console.log('✅ Wallet address:', wallet.address);
      
      // Store the wallet temporarily (we'll save it after password is set)
      setPendingWallet(wallet);
      
      // Show seed phrase to user FIRST (Step 1)
      setSeedPhrase(wallet.mnemonic.phrase);
      setShowSeedPhrase(true);
      
      console.log('✅ Showing seed phrase modal (Step 1 of 2)');
      return wallet;
      
    } catch (error) {
      console.error('❌ Failed to generate seed phrase:', error);
      setError('Failed to generate seed phrase: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Handle continue after seed phrase - show password modal
  const handleContinueAfterSeedPhrase = useCallback(() => {
    console.log('➡️ Continuing to password setup (STEP 2)...');
    setShowSeedPhrase(false);
    setShowPasswordModal(true);
  }, []);

  // FINAL STEP: Create wallet with password (after seed phrase is saved)
  const handleCreateNewAccountWithPassword = async (password) => {
    console.log('🔐 Creating account with password (STEP 2)...');
    
    if (!pendingWallet) {
      setError('No pending wallet found. Please start over.');
      return null;
    }
    
    if (!password) {
      setError('Please enter a password');
      return null;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const newAccount = {
        address: pendingWallet.address,
        privateKey: pendingWallet.privateKey,
        mnemonic: pendingWallet.mnemonic.phrase,
        publicKey: pendingWallet.publicKey,
        createdAt: new Date().toISOString(),
        name: `Zwallet Account ${localAccounts.length + 1}`,
        type: 'local'
      };

      console.log('✅ New account ready:', newAccount.address);
      
      // Save the account with password
      const updatedAccounts = [...localAccounts, newAccount];
      await SecureStorage.saveAccounts(updatedAccounts, password);
      setLocalAccounts(updatedAccounts);
      setHasExistingAccounts(true);

      // MARK WALLET AS CREATED
      localStorage.setItem('zwallet_created', 'true');
      
      // Notify parent about wallet creation
      if (onWalletCreated) {
        onWalletCreated(newAccount.address);
      }

      setShowPasswordModal(false);
      setAccountPassword('');
      setError('✅ Wallet created successfully!');
      
      console.log('✅ Account saved with password, connecting...');
      
      // Connect to the newly created account
      await connectLocalAccount(newAccount);
      setPendingWallet(null);
      
      return newAccount;
    } catch (error) {
      console.error('❌ Failed to create account with password:', error);
      setError('Failed to create account: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Handle unlock for connect - simplified to just call handleLoadLocalAccounts
  const handleUnlockForConnect = async (password) => {
    console.log('🔓 Unlocking wallet for connection...');
    return handleLoadLocalAccounts(password);
  };

  // Format USD value
  const formatUSD = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Calculate total portfolio value in USD
  const calculateTotalPortfolioValue = () => {
    const ethValue = parseFloat(balance) * ethPrice;
    const tokenValue = tokens.reduce((total, token) => {
      const tokenPrice = ['USDC', 'USDT', 'DAI'].includes(token.symbol) ? 1 : 0;
      return total + (parseFloat(token.balance) * tokenPrice);
    }, 0);
    return ethValue + tokenValue;
  };

  // Copy address to clipboard
  const copyAddress = () => {
    console.log('📋 Copying address to clipboard:', account);
    navigator.clipboard.writeText(account);
    setError('✅ Address copied to clipboard!');
    setTimeout(() => setError(''), 3000);
  };

  // Refresh account data
  const refreshAccountData = async () => {
    console.log('🔄 Manual refresh triggered');
    if (provider && account) {
      await updateAccountData(provider, account);
      setError('✅ Account data refreshed!');
      setTimeout(() => setError(''), 3000);
    } else {
      console.warn('⚠️ Cannot refresh: no provider or account');
      setError('❌ Cannot refresh: Not connected');
    }
  };

  // Enhanced send transaction function
  const sendTransaction = async () => {
    console.log('💸 Send transaction initiated:', { sendTo, sendAmount });

    if (!sendTo || !sendAmount) {
      setError('Please enter recipient address and amount');
      return;
    }

    if (parseFloat(sendAmount) > parseFloat(balance)) {
      setError('Insufficient balance');
      return;
    }

    if (!ethers.isAddress(sendTo)) {
      setError('Invalid Ethereum address');
      return;
    }

    // Set pending transaction and show confirmation modal
    setPendingTransaction({
      to: sendTo,
      value: sendAmount,
      from: account
    });
    setShowTransactionConfirm(true);
    setError('');

    console.log('✅ Transaction ready for confirmation');
  };

  // Poll transaction receipt
  const pollTransactionReceipt = useCallback(async (txHash, interval = 8000, maxAttempts = 30) => {
    if (!provider || !txHash) return null;

    if (pollingRef.current.has(txHash)) return null;
    pollingRef.current.add(txHash);

    try {
      let attempts = 0;
      while (attempts < maxAttempts && isMountedRef.current) {
        attempts++;

        try {
          const receipt = await provider.getTransactionReceipt(txHash);
          if (receipt) {
            if (isMountedRef.current) {
              setTransactionReceipt(prev => {
                const base = (prev && prev.hash === (receipt.transactionHash || txHash)) ? prev : {};
                return {
                  ...base,
                  hash: receipt.transactionHash || txHash,
                  status: receipt.status === 1 ? 'confirmed' : 'failed',
                  from: receipt.from || base.from,
                  to: receipt.to || base.to,
                  value: base.value || '0',
                  gasUsed: receipt.gasUsed?.toString?.() || base.gasUsed,
                  blockNumber: receipt.blockNumber || base.blockNumber
                };
              });

              // Refresh account balances
              try { await updateAccountData(provider, account); } catch (e) { /* ignore */ }
            }

            return receipt;
          }
        } catch (err) {
          // transient error; ignore and retry
        }

        await new Promise(res => setTimeout(res, interval));
      }
    } catch (err) {
      console.warn('[pollTransactionReceipt] unexpected error', err);
    } finally {
      pollingRef.current.delete(txHash);
    }

    return null;
  }, [provider, updateAccountData, account]);

  // Add token
  const addToken = async () => {
    console.log('🪙 Adding token:', newTokenAddress);
    try {
      const result = await addTokenHook(
        newTokenAddress,
        provider,
        account,
        ERC20_ABI,
        setError
      );

      if (result.success) {
        setNewTokenAddress('');
        setShowAddToken(false);
        setError('✅ Token added successfully!');
      }
    } catch (err) {
      console.error('❌ Failed to add token:', err);
      setError('❌ Failed to add token: ' + err.message);
    }
  };

  // Disconnect account
  const disconnectAccount = () => {
    console.log('🔌 Disconnecting account...');
    setAccount('');
    setBalance('0');
    setEthPrice(0);
    setProvider(null);
    setSigner(null);
    setIsConnected(false);
    setActiveLocalAccount(null);
    setAccountType(null);
    setError('');
    PasswordManager.clearSession();
    console.log('✅ Account disconnected');
  };

  // Switch Local Account Function - UPDATED for free RPCs
  const switchLocalAccount = async (accountAddress) => {
    console.log('🔄 Switching to account:', accountAddress);
    const acct = localAccounts.find(a => a.address === accountAddress);
    if (acct) {
      try {
        setLoading(true);
        
        // USE FREE PUBLIC RPC PROVIDER
        const currentProvider = provider || await getRPCProvider();
        const accountWithProvider = new ethers.Wallet(acct.privateKey, currentProvider);

        setProvider(currentProvider);
        setSigner(accountWithProvider);
        setAccount(acct.address);
        setActiveLocalAccount(acct);
        setAccountType('local');

        await updateAccountData(currentProvider, acct.address);
        setError('');
        console.log('✅ Account switched successfully');
      } catch (error) {
        console.error('❌ Failed to switch account:', error);
        setError('Failed to switch account: ' + error.message);
      } finally {
        setLoading(false);
      }
    } else {
      console.error('❌ Account not found:', accountAddress);
    }
  };

  // Delete Local Account Function
  const deleteLocalAccount = async (accountAddress, password) => {
    console.log('🗑️ Deleting account:', accountAddress);
    if (!window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      return;
    }

    try {
      const updatedAccounts = localAccounts.filter(a => a.address !== accountAddress);
      await SecureStorage.saveAccounts(updatedAccounts, password);
      setLocalAccounts(updatedAccounts);

      if (activeLocalAccount && activeLocalAccount.address === accountAddress) {
        disconnectAccount();
      }

      setError('✅ Account deleted successfully');
      setTimeout(() => setError(''), 3000);
      console.log('✅ Account deleted successfully');
    } catch (error) {
      console.error('❌ Failed to delete account:', error);
      setError('Failed to delete account: ' + error.message);
    }
  };

  // Import account from seed - CORRECTED FLOW
  const handleImportAccountFromSeed = async () => {
    console.log('📥 Starting import flow...');

    // FIX: Close all other modals first
    setShowSeedPhrase(false);
    setShowPasswordModal(false);
    setShowUnlockModal(false);
    setShowCreateAccountModal(false);
    setShowLocalAccountModal(false);

    // Then show import modal
    setShowImportAccountModal(true);

    console.log('✅ Import modal should now be visible');
  };

  // Import account final step with password
  const handleImportAccountFinalize = async (password) => {
    console.log('🔐 Finalizing import with password...');
    if (!importSeedPhrase.trim()) {
      setError('Please enter a seed phrase');
      return;
    }

    try {
      const { ethers: _ethers } = await import('ethers');
      const wallet = _ethers.Wallet.fromPhrase(importSeedPhrase.trim());
      const importedAccount = {
        address: wallet.address,
        privateKey: wallet.privateKey,
        mnemonic: wallet.mnemonic.phrase,
        publicKey: wallet.publicKey,
        createdAt: new Date().toISOString(),
        name: `Imported Account ${localAccounts.length + 1}`,
        type: 'local'
      };

      console.log('✅ Account imported:', importedAccount.address);

      const updatedAccounts = [...localAccounts, importedAccount];
      await SecureStorage.saveAccounts(updatedAccounts, password);
      setLocalAccounts(updatedAccounts);
      setHasExistingAccounts(true);

      // Mark wallet as created
      localStorage.setItem('zwallet_created', 'true');
      
      // Notify parent
      if (onWalletCreated) {
        onWalletCreated(importedAccount.address);
      }

      setShowImportAccountModal(false);
      setImportSeedPhrase('');
      setAccountPassword('');
      setError('✅ Account imported successfully!');

      await connectLocalAccount(importedAccount);

      console.log('✅ Import completed and connected');
      return importedAccount;
    } catch (error) {
      console.error('❌ Import failed:', error);
      setError('Invalid seed phrase: ' + error.message);
      return null;
    }
  };

    // FIXED: Load local accounts and AUTO-CONNECT after unlock
  const handleLoadLocalAccounts = async (password) => {
    console.log('🔓 Loading local accounts with password...');
    try {
      setIsUnlocking(true);
      setError('');

      const accounts = await SecureStorage.loadAccounts(password);
      console.log('📂 Accounts loaded:', accounts.length);
      setLocalAccounts(accounts);
      setHasExistingAccounts(accounts.length > 0);

      // Save session for auto-login
      PasswordManager.saveSession(password);
      console.log('💾 Session saved for auto-login');

      // CLOSE the unlock modal
      setShowUnlockModal(false);
      setAccountPassword('');

      // FIX: AUTO-CONNECT to the first account after successful unlock
      if (accounts.length > 0) {
        console.log('🔄 AUTO-CONNECTING after unlock to account:', accounts[0].address);

        // Small delay to ensure UI state updates before connecting
        setTimeout(async () => {
          try {
            await connectLocalAccount(accounts[0]);
            console.log('✅ Auto-connected after unlock - wallet UI should now show');
          } catch (connectError) {
            console.error('❌ Auto-connect failed:', connectError);
            setError('Connected but failed to load wallet UI: ' + connectError.message);
          }
        }, 100);
      } else {
        console.warn('⚠️ No accounts found after unlock');
        setError('No accounts found after unlock');
      }

      return accounts;
    } catch (error) {
      console.error('❌ Failed to load accounts:', error);
      setError('Failed to load accounts: ' + error.message);
      return [];
    } finally {
      setIsUnlocking(false);
    }
  };

  // Zwallet initialization function
  const initZwallet = useCallback(async () => {
    console.log('🚀 Initializing Zwallet...');
    
    // Add guard to prevent multiple initializations
    if (isMountedRef.current === false) return;
    
    if (SecureStorage.hasAccounts()) {
      console.log('📦 Existing accounts found, checking for session...');
      const session = PasswordManager.getSession();

      if (session) {
        console.log('🔑 Found existing session, loading accounts...');
        try {
          const accounts = await SecureStorage.loadAccounts(session.password);
          console.log('📂 Loaded accounts:', accounts.length);
          setLocalAccounts(accounts);

          if (accounts.length > 0) {
            console.log(`🔄 Found ${accounts.length} account(s), auto-connecting first account...`);
            await connectLocalAccount(accounts[0]);
            console.log('✅ Auto-connection completed');
          } else {
            console.warn('⚠️ No accounts found after decryption');
            setShowUnlockModal(true);
          }
        } catch (error) {
          console.warn('❌ Session invalid, showing unlock modal:', error.message);
          setShowUnlockModal(true);
        }
      } else {
        console.log('🔒 No session found, showing unlock modal');
        setShowUnlockModal(true);
      }
    } else {
      console.log('🆕 No existing accounts found, showing connection screen');
    }
  }, [connectLocalAccount]);

  // ============================================
  // 🔗 REGISTER ZWALLET WITH AUTHCONTEXT (WITH ENCRYPTION)
  // ============================================

  useEffect(() => {
    if (isConnected && account && signer && registerZwallet) {
      console.log('🔗 Zwallet: Registering with AuthContext for ENS + encryption integration');

      const zwalletApi = {
        // Original ENS integration functions
        sendTransaction: sendTransactionForENS,
        signMessage: signMessageForENS,
        getAddress: () => account,
        getProvider: () => provider,
        getSigner: () => signer,
        isConnected: () => isConnected,

        // 🔐 ADD THESE 3 LINES FOR ZHAT:
        encryptForZhat: async (r, m, o) => window.postMessage({source:'zhat-app',type:'ENCRYPT_MESSAGE_REQUEST',requestId:`e-${Date.now()}`,receiverAddress:r,message:m,options:o},'*'),

        decryptForZhat: async (s, e) => window.postMessage({source:'zhat-app',type:'DECRYPT_MESSAGE_REQUEST',requestId:`d-${Date.now()}`,senderAddress:s,encryptedData:e},'*'),

        getZhatEncryptionStatus: () => ({available:!!signer, ready:!!signer&&isConnected, publicKey:signer?.signingKey?.publicKey, address:account}),

        // Keep your existing encryption functions (optional - you might want both)
        encryptMessage: async (receiverAddress, message, options = {}) => {
          console.log('🔐 Zwallet API: Encrypting message for', receiverAddress);

          // Create a request ID for tracking
          const requestId = `encrypt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          return new Promise((resolve, reject) => {
            // Set up response listener
            const handleResponse = (event) => {
              if (!event.data || event.data.source !== 'zwallet') return;
              if (event.data.requestId === requestId) {
                window.removeEventListener('message', handleResponse);

                if (event.data.error) {
                  reject(new Error(event.data.error));
                } else {
                  resolve(event.data.encryptedData);
                }
              }
            };

            window.addEventListener('message', handleResponse);

            // Send encryption request (will be caught by our encryption handler)
            window.postMessage({
              source: 'zhat-app',
              type: 'ENCRYPT_MESSAGE_REQUEST',
              requestId,
              receiverAddress,
              message,
              options
            }, '*');

            // Timeout after 10 seconds
            setTimeout(() => {
              window.removeEventListener('message', handleResponse);
              reject(new Error('Encryption request timeout'));
            }, 10000);
          });
        },

        decryptMessage: async (senderAddress, encryptedData) => {
          console.log('🔓 Zwallet API: Decrypting message from', senderAddress);

          const requestId = `decrypt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          return new Promise((resolve, reject) => {
            const handleResponse = (event) => {
              if (!event.data || event.data.source !== 'zwallet') return;
              if (event.data.requestId === requestId) {
                window.removeEventListener('message', handleResponse);

                if (event.data.error) {
                  reject(new Error(event.data.error));
                } else {
                  resolve(event.data.decryptedMessage);
                }
              }
            };

            window.addEventListener('message', handleResponse);

            window.postMessage({
              source: 'zhat-app',
              type: 'DECRYPT_MESSAGE_REQUEST',
              requestId,
              senderAddress,
              encryptedData
            }, '*');

            setTimeout(() => {
              window.removeEventListener('message', handleResponse);
              reject(new Error('Decryption request timeout'));
            }, 10000);
          });
        },

        // Encryption status (keep existing)
        getEncryptionStatus: () => ({
          available: !!signer,
          initialized: !!signer?.signingKey?.publicKey,
          ready: !!signer && isConnected,
          publicKey: signer?.signingKey?.publicKey,
          address: account
        }),

        // Generate ephemeral key for forward secrecy
        generateEphemeralKey: async () => {
          const requestId = `ephkey-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          return new Promise((resolve, reject) => {
            const handleResponse = (event) => {
              if (!event.data || event.data.source !== 'zwallet') return;
              if (event.data.requestId === requestId) {
                window.removeEventListener('message', handleResponse);

                if (event.data.error) {
                  reject(new Error(event.data.error));
                } else {
                  resolve(event.data.ephemeralKey);
                }
              }
            };

            window.addEventListener('message', handleResponse);

            window.postMessage({
              source: 'zhat-app',
              type: 'GENERATE_EPHEMERAL_KEY',
              requestId
            }, '*');

            setTimeout(() => {
              window.removeEventListener('message', handleResponse);
              reject(new Error('Ephemeral key generation timeout'));
            }, 5000);
          });
        }
      };

      // Register with AuthContext
      registerZwallet(zwalletApi);

      // Also expose globally for cross-app communication
      window.__zwallet__ = zwalletApi;
      window.__zpost_wallet_api__ = zwalletApi;

      console.log('✅ Zwallet registered with AuthContext (ENS + encryption support)');
    }

    // ============================================
    // CLEANUP FUNCTION (Must be inside useEffect)
    // ============================================
    return () => {
      // Cleanup on disconnect or unmount
      if (!isConnected && registerZwallet) {
        registerZwallet(null);
        if (window.__zwallet__) {
          delete window.__zwallet__;
        }
        console.log('🗑️ Zwallet unregistered from AuthContext');
      }
    };
  }, [isConnected, account, signer, provider, registerZwallet, sendTransactionForENS, signMessageForENS]);

  // FIXED: Debug useEffect for button clicks
  useEffect(() => {
    const handleButtonClicks = (e) => {
      if (e.target.tagName === 'BUTTON') {
        console.log('🖱️ Button clicked:', e.target.textContent, e.target);
      }
    };
    
    document.addEventListener('click', handleButtonClicks);
    
    return () => {
      document.removeEventListener('click', handleButtonClicks);
    };
  }, []);

    // Add this useEffect to your Zwallet.js component (around line 1480):

  // FIXED: Handle mode changes without auto-closing modals
  useEffect(() => {
    console.log('🎯 Mode prop changed:', initialMode);

    // If mode changes to 'import' and we're not connected, show import modal
    if (initialMode === 'import' && !isConnected && !showImportAccountModal) {
      console.log('🔄 Switching to import mode, showing import modal');
      setShowImportAccountModal(true);

      // Reset other modals
      setShowSeedPhrase(false);
      setShowPasswordModal(false);
      setShowUnlockModal(false);
      setShowCreateAccountModal(false);
    }

    // IMPORTANT: Do NOT auto-close modals when mode changes
    // Let the user close them manually or when action is complete

  }, [initialMode, isConnected]);

  // FIXED: Simplified initialization effect
  useEffect(() => {
    console.log('🎯 Zwallet initialization useEffect running');
    console.log('Initial mode:', initialMode, 'isConnected:', isConnected, 'hasExistingAccounts:', hasExistingAccounts);

    // Prevent multiple runs
    if (initializationRun.current) {
      console.log('⏭️ Initialization already completed, skipping');
      return;
    }

    // Early return if already connected
    if (isConnected) {
      console.log('⚠️ Already connected, skipping initialization');
      initializationRun.current = true;
      return;
    }

    // Only auto-show unlock modal if we have existing accounts
    if (hasExistingAccounts && initialMode === 'connect') {
      console.log('🔓 Existing accounts found, auto-showing unlock modal');
      setTimeout(() => {
        setShowUnlockModal(true);
      }, 100);
    }

    initializationRun.current = true;

    return () => {
      initializationRun.current = false;
    };
  }, [initialMode, isConnected, hasExistingAccounts]);

  // FIXED: Add debug helpers
  useEffect(() => {
    // Only set up debug helpers in development
    if (typeof window !== 'undefined') {
      window._debugZwallet = {
        forceModal: (modalType) => {
          console.log('🔧 Debug: Forcing modal', modalType);
          switch(modalType) {
            case 'password':
              setShowPasswordModal(true);
              break;
            case 'unlock':
              setShowUnlockModal(true);
              break;
            case 'seed':
              setShowSeedPhrase(true);
              break;
            case 'import':
              setShowImportAccountModal(true);
              break;
            default:
              console.warn('Unknown modal type:', modalType);
          }
        },
        showState: () => {
          console.log('📊 Current Zwallet State:', {
            isConnected,
            account,
            hasExistingAccounts,
            showPasswordModal,
            showUnlockModal,
            showSeedPhrase,
            showImportAccountModal,
            initialMode,
            skipConnectionScreen
          });
        },
        simulateClick: (buttonText) => {
          const buttons = document.querySelectorAll('button');
          buttons.forEach(btn => {
            if (btn.textContent.includes(buttonText)) {
              console.log(`🖱️ Simulating click on "${buttonText}" button`);
              btn.click();
            }
          });
        },
        clearPending: () => {
          console.log('🧹 Clearing pending wallet');
          setPendingWallet(null);
          console.log('✅ Pending data cleared');
        },
        forceSeedPhrase: (phrase) => {
          console.log('🌱 Manually setting seed phrase');
          if (phrase) {
            setSeedPhrase(phrase);
            setShowSeedPhrase(true);
          } else {
            // Generate a random seed phrase
            const { ethers: _ethers } = require('ethers');
            const wallet = _ethers.Wallet.createRandom();
            setSeedPhrase(wallet.mnemonic.phrase);
            setShowSeedPhrase(true);
          }
        }
      };
      
      console.log('🔧 Debug helpers available: window._debugZwallet');
    }
    
    return () => {
      // Clean up on unmount
      if (typeof window !== 'undefined' && window._debugZwallet) {
        delete window._debugZwallet;
      }
    };
  }, [isConnected, account, hasExistingAccounts, showPasswordModal, showUnlockModal, showSeedPhrase, showImportAccountModal, initialMode, skipConnectionScreen]);

  // Mounted guard
  useEffect(() => {
    return () => { 
      isMountedRef.current = false;
      initializationRun.current = false;
    };
  }, []);

  const totalPortfolioValue = calculateTotalPortfolioValue();
  console.log('📊 Current state:', {
    isConnected,
    account,
    balance: parseFloat(balance),
    totalPortfolioValue,
    accountType,
    localAccounts: localAccounts.length,
    showSeedPhrase,
    showPasswordModal,
    showUnlockModal,
    showImportAccountModal
  });

  // FIXED: Render disconnected state - ALWAYS show connection screen first
  if (!isConnected) {
    // Show seed phrase modal if we have a pending wallet creation (Step 1)
    if (showSeedPhrase && seedPhrase) {
      return (
        <>
          <Helmet><title>Zwallet - Save Your Seed Phrase</title></Helmet>
          <SeedPhraseModal
            seedPhrase={seedPhrase}
            onClose={() => {
              setShowSeedPhrase(false);
              setSeedPhrase('');
              setPendingWallet(null);
              if (onClose) onClose();
            }}
            onContinue={handleContinueAfterSeedPhrase}
          />
        </>
      );
    }
    
    // Show password modal after seed phrase (Step 2)
    if (showPasswordModal) {
      return (
        <>
          <Helmet><title>Zwallet - Set Password</title></Helmet>
          <PasswordModal
            accountPassword={accountPassword}
            setAccountPassword={setAccountPassword}
            loading={loading}
            error={error}
            onCreateAccount={handleCreateNewAccountWithPassword}
            onClose={() => {
              setShowPasswordModal(false);
              setAccountPassword('');
              setPendingWallet(null);
              if (onClose) onClose();
            }}
          />
        </>
      );
    }
    
    // Show import modal if requested
    if (showImportAccountModal) {
      console.log('📥 Rendering import modal standalone');
      return (
        <>
          <Helmet><title>Zwallet - Import Existing Account</title></Helmet>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '100%',
              maxWidth: '500px',
              background: 'var(--card-bg)',
              borderRadius: '15px',
              border: '2px solid var(--gold)',
              boxShadow: '0 0 50px rgba(255, 215, 0, 0.3)',
              overflow: 'hidden'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'linear-gradient(90deg, var(--black) 0%, var(--card-bg) 100%)'
              }}>
                <h2 style={{ color: 'var(--gold)', margin: 0 }}>
                  📥 Import Zwallet
                </h2>
                <button
                  onClick={() => {
                    console.log('❌ Closing import modal');
                    setShowImportAccountModal(false);
                    setImportSeedPhrase('');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--gold)',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0 10px',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ✕
                </button>
              </div>
              
              {/* Modal Body - IMPORT FORM */}
              <div style={{ padding: '30px' }}>
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ color: 'var(--gold)', marginBottom: '15px' }}>
                    Enter Your Seed Phrase
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px' }}>
                    Enter your 12 or 24-word recovery phrase to import your wallet
                  </p>
                  
                  <textarea
                    placeholder="Enter your seed phrase here (12 or 24 words separated by spaces)"
                    value={importSeedPhrase}
                    onChange={(e) => setImportSeedPhrase(e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '15px',
                      background: 'var(--black)',
                      color: 'var(--gold)',
                      border: '1px solid var(--gold)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      resize: 'vertical',
                      marginBottom: '15px'
                    }}
                  />
                  
                  <div style={{
                    background: 'rgba(255, 215, 0, 0.05)',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid var(--gold)'
                  }}>
                    <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '12px', lineHeight: '1.5' }}>
                      <strong>⚠️ Security Note:</strong> Your seed phrase is never sent to our servers. It's only used locally to generate your wallet.
                    </p>
                  </div>
                </div>
                
                {/* Password Input for Import */}
                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', color: 'var(--gold)', marginBottom: '8px', fontWeight: 'bold' }}>
                    Set Local Password
                  </label>
                  <input
                    type="password"
                    placeholder="Enter a password to encrypt this wallet locally"
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--black)',
                      color: 'var(--gold)',
                      border: '1px solid var(--gold)',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '5px' }}>
                    This password only encrypts your wallet on this device
                  </div>
                </div>
              </div>
              
              {/* Modal Footer */}
              <div style={{
                padding: '20px',
                borderTop: '1px solid var(--border)',
                background: 'var(--black)'
              }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => {
                      if (!importSeedPhrase.trim()) {
                        setError('Please enter a seed phrase');
                        return;
                      }
                      if (!accountPassword) {
                        setError('Please enter a password');
                        return;
                      }
                      handleImportAccountFinalize(accountPassword);
                    }}
                    disabled={loading || !importSeedPhrase.trim() || !accountPassword}
                    style={{
                      flex: 1,
                      background: loading || !importSeedPhrase.trim() || !accountPassword ? '#666' : 'var(--gold)',
                      color: 'var(--black)',
                      border: 'none',
                      padding: '15px',
                      borderRadius: '8px',
                      cursor: loading || !importSeedPhrase.trim() || !accountPassword ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}
                  >
                    {loading ? 'Importing...' : '📥 Import Wallet'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowImportAccountModal(false);
                      setImportSeedPhrase('');
                      setAccountPassword('');
                    }}
                    style={{
                      flex: 1,
                      background: 'transparent',
                      color: '#ff4444',
                      border: '1px solid #ff4444',
                      padding: '15px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}
                  >
                    Cancel
                  </button>
                </div>
                
                {error && (
                  <div style={{
                    background: error.includes('✅') ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 68, 68, 0.1)',
                    border: error.includes('✅') ? '1px solid #00ff88' : '1px solid #ff4444',
                    color: error.includes('✅') ? '#00ff88' : '#ff4444',
                    padding: '12px',
                    borderRadius: '5px',
                    marginTop: '15px',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      );
    }
    
    // Show unlock modal if needed
    if (showUnlockModal) {
      return (
        <>
          <Helmet><title>Zwallet - Unlock Wallet</title></Helmet>
          <UnlockModal
            accountPassword={accountPassword}
            setAccountPassword={setAccountPassword}
            isUnlocking={isUnlocking}
            error={error}
            onUnlock={handleUnlockForConnect}
            onLoadAccounts={handleLoadLocalAccounts}
            onClose={() => {
              setShowUnlockModal(false);
              setAccountPassword('');
            }}
          />
        </>
      );
    }
    
    // FIXED: ALWAYS show the connection screen (BankSystem style)
    return (
      <>
        <Helmet><title>Zwallet - Secure Web3 Wallet</title></Helmet>
        
        {/* Show connection screen FIRST (this is the BankSystem-style entry point) */}
        <ZwalletConnection
          hasExistingAccounts={hasExistingAccounts}
          initZwallet={initZwallet}
          setShowUnlockModal={setShowUnlockModal}
          onGenerateSeedPhrase={handleGenerateSeedPhrase}
          onImportAccount={handleImportAccountFromSeed}
          onClose={onClose}
          zwalletMode={initialMode}
        />
        
        {/* All modals are now triggered from ZwalletConnection */}
        <ZwalletModals
          showPasswordModal={false}
          showUnlockModal={showUnlockModal}
          showImportAccountModal={false}
          showCreateAccountModal={showCreateAccountModal}
          showLocalAccountModal={showLocalAccountModal}
          showSendModal={false}
          showReceiveModal={false}
          showSecurityModal={false}
          showAddToken={false}
          showSeedPhrase={false}
          showTransactionConfirm={false}
          showTransactionReceipt={false}
          accountPassword={accountPassword}
          importSeedPhrase={importSeedPhrase}
          seedPhrase={seedPhrase}
          localAccounts={localAccounts}
          isUnlocking={isUnlocking}
          pendingTransaction={null}
          transactionReceipt={null}
          setShowPasswordModal={setShowPasswordModal}
          setShowUnlockModal={setShowUnlockModal}
          setShowImportAccountModal={setShowImportAccountModal}
          setShowCreateAccountModal={setShowCreateAccountModal}
          setShowLocalAccountModal={setShowLocalAccountModal}
          setShowSeedPhrase={setShowSeedPhrase}
          setShowTransactionConfirm={setShowTransactionConfirm}
          setShowTransactionReceipt={setShowTransactionReceipt}
          setAccountPassword={setAccountPassword}
          setImportSeedPhrase={setImportSeedPhrase}
          setSeedPhrase={setSeedPhrase}
          setIsUnlocking={setIsUnlocking}
          setPendingTransaction={setPendingTransaction}
          setTransactionReceipt={setTransactionReceipt}
          createNewAccount={handleCreateNewAccountWithPassword}
          importAccountFromSeed={handleImportAccountFinalize}
          loadLocalAccounts={handleLoadLocalAccounts}
          connectLocalAccount={connectLocalAccount}
          handleUnlockForConnect={handleUnlockForConnect}
          switchLocalAccount={switchLocalAccount}
          deleteLocalAccount={deleteLocalAccount}
          confirmTransaction={confirmTransaction}
          cancelTransaction={cancelTransaction}
          closeReceipt={closeReceipt}
          account={''}
          balance={'0'}
          ethPrice={0}
          sendTo={''}
          sendAmount={''}
          newTokenAddress={''}
          loading={loading}
          error={error}
          setError={setError}
          setShowSendModal={() => {}}
          setShowReceiveModal={() => {}}
          setShowSecurityModal={() => {}}
          setShowAddToken={() => {}}
          sendTransaction={() => {}}
          addToken={() => {}}
          copyAddress={() => {}}
          disconnectAccount={() => {}}
          formatUSD={formatUSD}
        />
      </>
    );
  }

  // Render connected state
  return (
    <section className={`zwallet-container ${embedded ? 'embedded' : ''}`}>
      <Helmet><title>Zwallet - {formatUSD(totalPortfolioValue)} | Your Web3 Wallet</title></Helmet>

      <div style={{ background: 'var(--black)', padding: '30px', borderRadius: '15px', boxShadow: '0 0 30px var(--gold)', maxWidth: '1200px', margin: '20px auto', transition: 'box-shadow 0.3s ease' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <h1 style={{ fontSize: '28px', color: 'var(--gold)', margin: '0 0 15px 0' }}>
              <a href="/" style={{ color: 'var(--gold)', textDecoration: 'none' }}>🏦 Zwallet</a>
            </h1>
            <p style={{ color: 'var(--text-secondary)', margin: '5px 0 0 0', fontSize: '14px' }}>{network?.name ? `Network: ${network.name}` : 'Ethereum Mainnet'} • 🔐 Secure Web3 Wallet</p>
          </div>

          <div style={{ background: 'var(--card-bg)', padding: '15px', borderRadius: '8px', border: '1px solid var(--gold)', minWidth: '300px' }}>
            <GlobalNav />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#00FF00', animation: 'pulse 2s infinite' }}></div>
            <span style={{ color: '#00FF00', fontSize: '14px', fontWeight: 'bold' }}>CONNECTED</span>
            <div style={{ background: 'var(--gold)', color: 'var(--black)', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>🌐 Mainnet</div>
            {accountType === 'local' && localAccounts.length > 1 && <button onClick={() => setShowLocalAccountModal(true)} style={{ background: 'transparent', color: 'var(--gold)', border: '1px solid var(--gold)', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Switch Account</button>}
            <button onClick={disconnectAccount} style={{ background: 'transparent', color: '#ff4444', border: '1px solid #ff4444', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Disconnect</button>
          </div>
        </div>

        <div style={{ background: 'var(--card-bg)', padding: '30px', borderRadius: '12px', border: '2px solid var(--gold)', marginBottom: '30px', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', margin: '0 0 10px 0', fontSize: '16px' }}>Total Portfolio Value</p>
          <h2 style={{ fontSize: '48px', color: 'var(--gold)', margin: '0 0 10px 0', fontWeight: 'bold', textShadow: '0 0 10px var(--gold-shadow)' }}>{formatUSD(totalPortfolioValue)}</h2>
          <p style={{ color: 'var(--text-secondary)', margin: '0 0 20px 0', fontSize: '16px' }}>{parseFloat(balance || '0').toFixed(6)} {network?.currency || 'ETH'} • ${(parseFloat(balance || '0') * (ethPrice || 3500)).toFixed(2)} USD</p>

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setShowSendModal(true)} style={{ background: 'var(--gold)', color: 'var(--black)', border: 'none', padding: '15px 25px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', minWidth: '120px' }}>💸 Send</button>
            <button onClick={() => setShowReceiveModal(true)} style={{ background: 'transparent', color: 'var(--gold)', border: '2px solid var(--gold)', padding: '15px 25px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', minWidth: '120px' }}>📥 Receive</button>
            <button onClick={copyAddress} style={{ background: 'transparent', color: 'var(--gold)', border: '2px solid var(--gold)', padding: '15px 25px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', minWidth: '120px' }}>📋 Copy Address</button>
          </div>

          <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '8px', marginTop: '20px', fontSize: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <strong style={{ color: 'var(--gold)' }}>Wallet Identity:</strong>
                  <span style={{ color: 'var(--text-light)', fontFamily: 'monospace', fontSize: '14px' }}>{formatAddressWithENS(account)}</span>
                </div>
                {connectedENSName && <div style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid var(--gold)', padding: '8px', borderRadius: '6px', marginTop: '10px', textAlign: 'center' }}><strong style={{ color: 'var(--gold)' }}>ENS Identity:</strong><div style={{ color: 'var(--text-light)', fontFamily: 'monospace', fontSize: '14px', marginTop: '4px' }}>🌐 {connectedENSName}</div></div>}
                <div style={{ marginTop: '8px', color: 'var(--text-secondary)', fontSize: '12px' }}>Network: <strong>{network?.name || 'Ethereum Mainnet'}</strong> • Chain ID: <strong>{network?.chainId || '1'}</strong></div>
              </div>

              <button onClick={refreshAccountData} disabled={loading} style={{ background: 'var(--gold)', color: 'var(--black)', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '12px', opacity: loading ? 0.6 : 1, minWidth: '80px' }}>{loading ? '🔄' : '🔄 Refresh'}</button>
            </div>
          </div>
        </div>

        <ZwalletTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          balance={balance}
          ethPrice={ethPrice}
          tokens={tokens}
          txHistory={txHistory}
          formatHash={formatHash}
          formatAddress={formatAddress}
          formatUSD={formatUSD}
          setShowAddToken={setShowAddToken}
          setShowSecurityModal={setShowSecurityModal}
          setShowSendModal={setShowSendModal}
          loading={loading}
          refreshAccountData={refreshAccountData}
          account={account}
        />

        {error && <div style={{ background: error.includes('✅') ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 68, 68, 0.1)', border: error.includes('✅') ? '1px solid #00ff88' : '1px solid #ff4444', color: error.includes('✅') ? '#00ff88' : '#ff4444', padding: '15px', borderRadius: '8px', marginTop: '20px', fontSize: '14px', textAlign: 'center' }}>{error}</div>}
      </div>

      <ZwalletModals
        showSendModal={showSendModal}
        showReceiveModal={showReceiveModal}
        showSecurityModal={showSecurityModal}
        showAddToken={showAddToken}
        showPasswordModal={false}
        showUnlockModal={false}
        showCreateAccountModal={showCreateAccountModal}
        showImportAccountModal={showImportAccountModal}
        showLocalAccountModal={showLocalAccountModal}
        showSeedPhrase={false}
        showTransactionConfirm={showTransactionConfirm}
        showTransactionReceipt={showTransactionReceipt}
        sendTo={sendTo}
        sendAmount={sendAmount}
        newTokenAddress={newTokenAddress}
        accountPassword={accountPassword}
        importSeedPhrase={importSeedPhrase}
        seedPhrase={seedPhrase}
        localAccounts={localAccounts}
        isUnlocking={isUnlocking}
        pendingTransaction={pendingTransaction}
        transactionReceipt={transactionReceipt}
        account={account}
        balance={balance}
        ethPrice={ethPrice}
        setShowSendModal={setShowSendModal}
        setShowReceiveModal={setShowReceiveModal}
        setShowSecurityModal={setShowSecurityModal}
        setShowAddToken={setShowAddToken}
        setShowPasswordModal={setShowPasswordModal}
        setShowUnlockModal={setShowUnlockModal}
        setShowCreateAccountModal={setShowCreateAccountModal}
        setShowImportAccountModal={setShowImportAccountModal}
        setShowLocalAccountModal={setShowLocalAccountModal}
        setShowSeedPhrase={setShowSeedPhrase}
        setShowTransactionConfirm={setShowTransactionConfirm}
        setShowTransactionReceipt={setShowTransactionReceipt}
        setSendTo={setSendTo}
        setSendAmount={setSendAmount}
        setNewTokenAddress={setNewTokenAddress}
        setAccountPassword={setAccountPassword}
        setImportSeedPhrase={setImportSeedPhrase}
        setSeedPhrase={setSeedPhrase}
        setIsUnlocking={setIsUnlocking}
        setPendingTransaction={setPendingTransaction}
        setTransactionReceipt={setTransactionReceipt}
        createNewAccount={handleCreateNewAccountWithPassword}
        importAccountFromSeed={handleImportAccountFinalize}
        loadLocalAccounts={handleLoadLocalAccounts}
        connectLocalAccount={connectLocalAccount}
        handleUnlockForConnect={handleUnlockForConnect}
        switchLocalAccount={switchLocalAccount}
        deleteLocalAccount={deleteLocalAccount}
        confirmTransaction={confirmTransaction}
        cancelTransaction={cancelTransaction}
        closeReceipt={closeReceipt}
        sendTransaction={sendTransaction}
        addToken={addToken}
        copyAddress={copyAddress}
        disconnectAccount={disconnectAccount}
        loading={loading}
        error={error}
        setError={setError}
        formatUSD={formatUSD}
      />
    </section>
  );
};


export default Zwallet;
